import type { CampaignModulesData } from 'bundles/campaigns/types/CampaignModulesData';

type OverrideKey = 'cannedCollectionsOverride' | 'cardTakeoverOverride' | 'collectionsOverride' | 'heroBentoOverride';

type ModuleWithAdditionalData = {
  contentDataJson?: unknown;
  matchedCampaign?: { additionalData?: ReadonlyArray<unknown> | null } | null;
};

// Which campaign module each Contentful override replaces.
const MODULE_OVERRIDE_KEYS: Array<[keyof CampaignModulesData, OverrideKey]> = [
  ['cannedCollectionModule', 'cannedCollectionsOverride'],
  ['cardTakeoverModule', 'cardTakeoverOverride'],
  ['collectionsModule', 'collectionsOverride'],
  ['heroModule', 'heroBentoOverride'],
];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const getOverrideContent = (module: ModuleWithAdditionalData | undefined, key: OverrideKey): unknown => {
  const entries = module?.matchedCampaign?.additionalData ?? [];
  for (const entry of entries) {
    const override = isRecord(entry) ? entry[key] : undefined;
    if (isRecord(override) && override.contentDataJson) return override.contentDataJson;
  }
  return undefined;
};

/**
 * Replaces each module's contentDataJson with the matching Contentful override content when the matched campaign
 * carries one. Modules without an override are returned untouched.
 */
const applyContentfulOverrides = (campaignData: CampaignModulesData | undefined): CampaignModulesData | undefined => {
  if (!campaignData) return campaignData;

  let result = campaignData;
  MODULE_OVERRIDE_KEYS.forEach(([moduleKey, overrideKey]) => {
    const module = campaignData[moduleKey];
    const overrideContent = getOverrideContent(module, overrideKey);
    if (module && overrideContent) {
      result = { ...result, [moduleKey]: { ...module, contentDataJson: overrideContent } };
    }
  });
  return result;
};

export default applyContentfulOverrides;
